import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/utils/AuthContext';
import { updateProfile, changePassword, deleteAccount, linkTelegram, unlinkTelegram, type TelegramAuthData } from '@/utils/auth-pb';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { TelegramLoginButton } from '@/components/ui/TelegramLoginButton';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';

export default function ProfilePage() {
  const navigate = useLocalizedNavigate();
  const { user, refreshUser, logout } = useAuth();
  const { t } = useTranslation('profile');

  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isLinkingTelegram, setIsLinkingTelegram] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const isTelegramLinked = !!user?.telegram_id;
  const isTelegramOnlyAccount = user?.email?.endsWith('@telegram.user') ?? false;

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Validate passwords if changing
    if (password || confirmPassword) {
      if (!currentPassword) {
        setError(t('validation.enterCurrentPassword'));
        return;
      }
      if (password !== confirmPassword) {
        setError(t('validation.passwordsMismatch'));
        return;
      }
      if (password.length < 8) {
        setError(t('validation.passwordMinLength'));
        return;
      }
    }

    try {
      setIsUpdating(true);
      setError('');
      setSuccess('');

      // Update profile name
      await updateProfile(user.id, { name });

      // Change password if provided
      if (password) {
        await changePassword(user.id, currentPassword, password);
        setCurrentPassword('');
        setPassword('');
        setConfirmPassword('');
      }

      await refreshUser();
      setSuccess(t('success.saved'));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('error.saveFailed');
      setError(message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleTelegramLink = async (data: TelegramAuthData) => {
    setIsLinkingTelegram(true);
    setError('');
    setSuccess('');
    try {
      await linkTelegram(data);
      await refreshUser();
      setSuccess(t('telegram.linkSuccess'));
    } catch (err) {
      setError((err as { message: string }).message || t('telegram.linkError'));
    } finally {
      setIsLinkingTelegram(false);
    }
  };

  const handleTelegramUnlink = async () => {
    if (!confirm(t('telegram.unlinkConfirm'))) return;
    setIsLinkingTelegram(true);
    setError('');
    setSuccess('');
    try {
      await unlinkTelegram();
      await refreshUser();
      setSuccess(t('telegram.unlinkSuccess'));
    } catch (err) {
      setError((err as { message: string }).message || t('telegram.unlinkError'));
    } finally {
      setIsLinkingTelegram(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== t('deleteAccount.confirmWord')) {
      setError(t('deleteAccount.confirmError'));
      return;
    }

    if (!user) return;

    try {
      setIsDeleting(true);
      await deleteAccount(user.id);
      logout();
      navigate('/login');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('deleteAccount.error');
      setError(message);
      setShowDeleteDialog(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <div className="w-full max-w-[500px]">
        {/* Заголовок */}
        <h1 className="font-manrope font-bold text-[32px] leading-[40px] text-[color:var(--black-text,#0d0d0d)] mb-[40px]">
          {t('title')}
        </h1>

        {/* Уведомления */}
        {error && (
          <div className="rounded-[16px] bg-[#fff0f0] px-[16px] py-[12px] font-segoe text-[14px] leading-[20px] text-[#ff5b63] mb-[16px]">
            {error}
          </div>
        )}

        {success && (
          <div className="rounded-[16px] bg-[#e8f8e8] px-[16px] py-[12px] font-segoe text-[14px] leading-[20px] text-[#4caf50] mb-[16px]">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Поля формы */}
          <div className="flex flex-col gap-[16px] mb-[24px]">
            {/* Имя */}
            <div className="flex flex-col gap-[8px]">
              <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
                {t('form.nameLabel')}
              </label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isUpdating}
                className="h-[48px] bg-transparent border border-[#eeeeee] rounded-[8px] px-[16px] font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] placeholder:text-[color:var(--grey-text,#565858)] focus:border-[var(--main-brand,#2197f1)]"
                placeholder={t('form.namePlaceholder')}
              />
            </div>

            {/* Почта */}
            <div className="flex flex-col gap-[8px]">
              <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
                {t('form.emailLabel')}
              </label>
              <Input
                type="email"
                value={email}
                disabled
                className="h-[48px] bg-transparent border border-[#eeeeee] rounded-[8px] px-[16px] font-segoe text-[16px] leading-[24px] text-[color:var(--grey-text,#565858)] cursor-not-allowed"
              />
            </div>

            {/* Текущий пароль */}
            <div className="flex flex-col gap-[8px]">
              <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
                {t('form.currentPasswordLabel')}
              </label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={isUpdating}
                  className="h-[48px] bg-transparent border border-[#eeeeee] rounded-[8px] px-[16px] pr-[48px] font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] placeholder:text-[color:var(--grey-text,#565858)] focus:border-[var(--main-brand,#2197f1)]"
                  placeholder={t('form.currentPasswordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-[16px] top-1/2 -translate-y-1/2 text-[color:var(--grey-text,#565858)] hover:text-[color:var(--black-text,#0d0d0d)]"
                >
                  {showCurrentPassword ? (
                    <Eye className="h-5 w-5" />
                  ) : (
                    <EyeOff className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Новый пароль */}
            <div className="flex flex-col gap-[8px]">
              <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
                {t('form.newPasswordLabel')}
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isUpdating}
                  className="h-[48px] bg-transparent border border-[#eeeeee] rounded-[8px] px-[16px] pr-[48px] font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] placeholder:text-[color:var(--grey-text,#565858)] focus:border-[var(--main-brand,#2197f1)]"
                  placeholder={t('form.newPasswordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-[16px] top-1/2 -translate-y-1/2 text-[color:var(--grey-text,#565858)] hover:text-[color:var(--black-text,#0d0d0d)]"
                >
                  {showPassword ? (
                    <Eye className="h-5 w-5" />
                  ) : (
                    <EyeOff className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Подтвердите пароль */}
            <div className="flex flex-col gap-[8px]">
              <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
                {t('form.confirmPasswordLabel')}
              </label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isUpdating}
                  className="h-[48px] bg-transparent border border-[#eeeeee] rounded-[8px] px-[16px] pr-[48px] font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] placeholder:text-[color:var(--grey-text,#565858)] focus:border-[var(--main-brand,#2197f1)]"
                  placeholder={t('form.confirmPasswordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-[16px] top-1/2 -translate-y-1/2 text-[color:var(--grey-text,#565858)] hover:text-[color:var(--black-text,#0d0d0d)]"
                >
                  {showConfirmPassword ? (
                    <Eye className="h-5 w-5" />
                  ) : (
                    <EyeOff className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Кнопка Сохранить */}
          <Button
            type="submit"
            disabled={isUpdating}
            className="w-full h-[48px] bg-[var(--accent-background,rgba(33,151,241,0.2))] hover:bg-[#2197f1] hover:text-white text-[color:var(--main-brand,#2197f1)] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] transition-colors"
          >
            {isUpdating ? (
              <span className="inline-flex items-center gap-[8px]">
                <Loader2 className="h-5 w-5 animate-spin" />
                {t('form.saving')}
              </span>
            ) : (
              t('form.save')
            )}
          </Button>
        </form>

        {/* Telegram */}
        <div className="mt-[24px] border-t border-[#eeeeee] pt-[24px] flex flex-col gap-[8px]">
          {isTelegramLinked ? (
            <div className="flex items-center justify-between rounded-[8px] border border-[#eeeeee] px-[16px] py-[12px]">
              <span className="font-segoe text-[14px] text-[color:var(--black-text,#0d0d0d)]">
                {t('telegram.linked', { id: user?.telegram_id })}
              </span>
              {!isTelegramOnlyAccount && (
                <Button
                  type="button"
                  onClick={handleTelegramUnlink}
                  disabled={isLinkingTelegram}
                  className="h-[36px] px-[16px] bg-transparent border border-[#eeeeee] text-[#565858] rounded-[8px] text-[13px] hover:bg-[#fff0f0] hover:text-[#ff5b63] transition-colors"
                >
                  {isLinkingTelegram ? t('telegram.unlinking') : t('telegram.unlink')}
                </Button>
              )}
            </div>
          ) : (
            <div>
              <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)] mb-[8px]">
                {t('telegram.linkHint')}
              </p>
              <TelegramLoginButton onAuth={handleTelegramLink} size="medium" label={t('telegram.linkButton')} />
            </div>
          )}
        </div>

        {/* Кнопки действий */}
        <div className="flex gap-[16px] mt-[24px] border-t border-[#eeeeee] pt-[24px]">
          <Button
            type="button"
            onClick={() => setShowDeleteDialog(true)}
            className="flex-1 h-[48px] bg-[var(--error-outline,#ff9196)] hover:bg-[#ff5b63] text-[color:var(--white,#ffffff)] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] transition-colors"
          >
            {t('deleteAccount.button')}
          </Button>

          <Button
            type="button"
            onClick={handleLogout}
            className="flex-1 h-[48px] bg-[var(--accent-background,rgba(33,151,241,0.2))] hover:bg-[#2197f1] hover:text-white text-[color:var(--main-brand,#2197f1)] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] transition-colors"
          >
            {t('logout')}
          </Button>
        </div>
      </div>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#0d0d0d]">
              {t('deleteAccount.title')}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#565858]">
              {t('deleteAccount.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm text-[#565858] leading-5">
                {t('deleteAccount.confirmLabel')}
              </label>
              <Input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                disabled={isDeleting}
                className="h-12 bg-transparent border border-[#eeeeee] rounded-2xl px-4 text-base text-[#0d0d0d] placeholder:text-[#cecdc8]"
                placeholder={t('deleteAccount.confirmWord')}
              />
            </div>
          </div>

          <DialogFooter className="flex gap-3 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteConfirmation('');
              }}
              disabled={isDeleting}
              className="flex-1 rounded-2xl h-12 border-[#e5e5e5]"
            >
              {t('deleteAccount.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleDeleteAccount}
              disabled={isDeleting || deleteConfirmation !== t('deleteAccount.confirmWord')}
              className="flex-1 bg-[#ff9196] hover:bg-[#ff5b63] text-white rounded-2xl h-12 transition-colors"
            >
              {isDeleting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('deleteAccount.deleting')}
                </span>
              ) : (
                t('deleteAccount.confirm')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
